import React, { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock3, GitBranch, Shuffle, Bookmark, X, RefreshCw, Map, GitMerge, ZoomIn, ZoomOut, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { CardData } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { buildYamazumiColumns, getCycleTimeMinutes, simularDemanda, calcularLeadTime, detectarGargalo, UNASSIGNED_ROLE_ID } from './YamazumiSimulator';

interface YamazumiPanelProps {
  isOpen: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClose: () => void;
  cards: CardData[];
  assignments: Record<string, string>;
  shiftMinutes: number;
  fteByRole: Record<string, number>;
  pendingChanges: number;
  onShiftMinutesChange: (minutes: number) => void;
  onFteChange: (roleKey: string, nextValue: number) => void;
  onSyncToMap: () => void;
  onAutoBalance?: () => void;
  onSaveBaseline?: () => void;
  savedBaseline?: boolean;
  onSyncToExecution?: () => void | Promise<void>;
  syncingToExecution?: boolean;
  /** Controlled zoom level — synced with YamazumiSimulator */
  zoom?: number;
  onZoomChange?: (z: number) => void;
  /** Demand simulation % — synced with YamazumiSimulator */
  demandaPercent?: number;
  onDemandaPercentChange?: (v: number) => void;
  /** Snapshot of saved baseline KPIs for Δ BASE display */
  savedBaselineKpis?: { occupation: number; leadMinutes: number } | null;
}

export const YamazumiPanel: React.FC<YamazumiPanelProps> = ({
  isOpen,
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  cards,
  assignments,
  shiftMinutes,
  fteByRole,
  pendingChanges,
  onShiftMinutesChange,
  onFteChange,
  onSyncToMap,
  onAutoBalance,
  onSaveBaseline,
  savedBaseline,
  onSyncToExecution,
  syncingToExecution = false,
  zoom = 1.0,
  onZoomChange,
  demandaPercent = 0,
  onDemandaPercentChange,
  savedBaselineKpis,
}) => {
  const { organization } = useProjectStore();

  const activityCards = useMemo(
    () => cards.filter((card) => card.tipo === 'atividade' && card.status === 'ativo'),
    [cards]
  );

  const columns = useMemo(
    () => buildYamazumiColumns(activityCards, organization?.roles ?? []),
    [activityCards, organization?.roles]
  );

  // Local buffered state so typing is fluid; syncs from prop when prop changes externally
  const [localShift, setLocalShift] = useState(String(shiftMinutes));
  useEffect(() => { setLocalShift(String(shiftMinutes)); }, [shiftMinutes]);

  const [localFte, setLocalFte] = useState<Record<string, string>>({});
  useEffect(() => {
    setLocalFte((prev) => {
      const next: Record<string, string> = { ...prev };
      for (const col of columns) {
        const canonical = String(Math.max(1, Number(fteByRole[col.id] ?? 1)));
        if (!(col.id in prev)) next[col.id] = canonical;
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // ── KPI computation ────────────────────────────────────────────────────────
  const totalsByColumn = useMemo(() => {
    const result: Record<string, number> = {};
    for (const col of columns) result[col.id] = 0;
    for (const card of activityCards) {
      const colId = assignments[card.id] ?? UNASSIGNED_ROLE_ID;
      result[colId] = (result[colId] ?? 0) + getCycleTimeMinutes(card);
    }
    return result;
  }, [activityCards, assignments, columns]);

  const simulatedTotalsByColumn = useMemo(
    () => (demandaPercent > 0 ? simularDemanda(totalsByColumn, demandaPercent) : totalsByColumn),
    [totalsByColumn, demandaPercent],
  );

  const kpiData = useMemo(() => {
    const assignedColumns = columns.filter((c) => c.id !== UNASSIGNED_ROLE_ID);
    let overallLoad = 0;
    let overallCapacity = 0;
    let bottleneckCount = 0;
    for (const col of assignedColumns) {
      const load = simulatedTotalsByColumn[col.id] ?? 0;
      const fte = Math.max(1, Number(fteByRole[col.id] ?? 1));
      const capacity = shiftMinutes * fte;
      overallLoad += load;
      overallCapacity += capacity;
      if (detectarGargalo(load, capacity)) bottleneckCount++;
    }
    const overallOccupation = overallCapacity > 0 ? Math.round((overallLoad / overallCapacity) * 100) : 0;
    const ociosidade = Math.max(0, 100 - overallOccupation);
    const leadTimeMinutes = calcularLeadTime(simulatedTotalsByColumn);
    return { overallOccupation, ociosidade, bottleneckCount, leadTimeMinutes };
  }, [columns, simulatedTotalsByColumn, fteByRole, shiftMinutes]);
  if (!isOpen) return null;

  if (isCollapsed) {
    return (
      <div className="flex h-full w-full flex-col items-center bg-[var(--bg-workspace)]">
        <div className="flex w-full items-center justify-center border-b border-slate-600/70 px-1 py-2">
          <button onClick={onToggleCollapse} className="rounded p-1 transition-colors hover:bg-white/10" title="Expandir controles">
            <ChevronLeft size={14} />
          </button>
        </div>
        <div className="mt-4 rotate-180 text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]" style={{ writingMode: 'vertical-rl' }}>
          Yamazumi
        </div>
        <div className="mt-auto mb-3 text-[10px] text-[var(--text-secondary)]">{pendingChanges}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-[var(--bg-workspace)]">
      {/* Header — context only */}
      <div className="relative border-b border-slate-600/70 px-3" style={{ height: '44px', minHeight: '44px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '2px', paddingBottom: '2px' }}>
        <div className="absolute right-2 top-1 flex items-center gap-0.5">
          {onToggleCollapse && (
            <button onClick={onToggleCollapse} className="rounded p-1 transition-colors hover:bg-white/10" title="Colapsar controles">
              <ChevronRight size={14} />
            </button>
          )}
          <button onClick={onClose} className="rounded p-1 transition-colors hover:bg-white/10">
            <X size={14} />
          </button>
        </div>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--text-primary)] leading-none">
          Controles Yamazumi
        </h2>
        <p className="mt-1 text-[10px] leading-none text-[var(--text-secondary)]">
          {columns.filter((c) => c.id !== UNASSIGNED_ROLE_ID).length} cargos · {activityCards.length} atividades
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto px-3 py-3">

        {/* ── KPI Strip ───────────────────────────────────────────────── */}
        <section className="border border-slate-600/70 bg-[var(--bg-surface)] px-2 py-2">
          <div className="grid grid-cols-5 gap-px">
            {/* EFIC */}
            <div className="flex flex-col items-center py-1.5 px-1">
              <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)] leading-none mb-1">EFIC</span>
              <span className={`text-[13px] font-bold tabular-nums leading-none ${kpiData.overallOccupation > 100 ? 'text-red-400' : kpiData.overallOccupation >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {kpiData.overallOccupation}%
              </span>
            </div>
            {/* OCIO */}
            <div className="flex flex-col items-center py-1.5 px-1">
              <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)] leading-none mb-1">OCIO</span>
              <span className="text-[13px] font-bold tabular-nums leading-none text-[var(--text-secondary)]">
                {kpiData.ociosidade}%
              </span>
            </div>
            {/* GARG */}
            <div className="flex flex-col items-center py-1.5 px-1">
              <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)] leading-none mb-1">GARG</span>
              <span className={`text-[13px] font-bold tabular-nums leading-none ${kpiData.bottleneckCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {kpiData.bottleneckCount}
              </span>
            </div>
            {/* LEAD */}
            <div className="flex flex-col items-center py-1.5 px-1">
              <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)] leading-none mb-1">LEAD</span>
              <span className={`text-[13px] font-bold tabular-nums leading-none ${kpiData.leadTimeMinutes > shiftMinutes ? 'text-red-400' : kpiData.leadTimeMinutes >= shiftMinutes * 0.85 ? 'text-amber-400' : 'text-[var(--text-primary)]'}`}>
                {kpiData.leadTimeMinutes >= 60 ? `${(kpiData.leadTimeMinutes / 60).toFixed(1)}h` : `${Math.round(kpiData.leadTimeMinutes)}m`}
              </span>
            </div>
            {/* Δ BASE */}
            <div className="flex flex-col items-center py-1.5 px-1">
              <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)] leading-none mb-1">Δ BASE</span>
              {savedBaselineKpis != null ? (() => {
                const delta = kpiData.overallOccupation - savedBaselineKpis.occupation;
                const improved = delta >= 0;
                return (
                  <span className={`text-[11px] font-bold tabular-nums leading-none ${improved ? 'text-emerald-400' : 'text-red-400'}`}>
                    {delta >= 0 ? '+' : ''}{delta}%
                  </span>
                );
              })() : (
                <span className="text-[13px] font-bold leading-none text-[var(--text-muted)]">—</span>
              )}
            </div>
          </div>
          {demandaPercent > 0 && (
            <div className="mt-1.5 border-t border-slate-600/40 pt-1.5 text-center">
              <span className="text-[9px] font-semibold text-amber-400 uppercase tracking-wider">Simulação ativa: +{demandaPercent}% demanda</span>
            </div>
          )}
        </section>

        {/* ── Tempo de turno ─────────────────────────────────────────── */}
        <section className="border border-slate-600/70 bg-[var(--bg-surface)] px-3 py-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold text-[var(--text-primary)]">
            <Clock3 size={14} /> Tempo de turno
          </div>
          <input
            type="number"
            min={60}
            step={30}
            value={localShift}
            onChange={(e) => setLocalShift(e.target.value)}
            onBlur={() => {
              const val = Math.max(60, Number(localShift) || 480);
              setLocalShift(String(val));
              onShiftMinutesChange(val);
            }}
            className="w-full border border-slate-600/70 bg-[var(--bg-app)] px-2 py-2 text-[12px] text-[var(--text-primary)] focus:border-blue-500 focus:outline-none"
          />
          <p className="mt-1 text-[10px] text-[var(--text-secondary)]">Capacidade base por operador em minutos.</p>
        </section>

        {/* ── FTE por cargo ───────────────────────────────────────────── */}
        <section className="border border-slate-600/70 bg-[var(--bg-surface)] px-3 py-3">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold text-[var(--text-primary)]">
            <GitBranch size={14} /> FTE por cargo
          </div>
          <div className="space-y-2">
            {columns.filter((col) => col.id !== UNASSIGNED_ROLE_ID).map((column) => {
              const assignedCount = activityCards.filter((card) => (assignments[card.id] ?? UNASSIGNED_ROLE_ID) === column.id).length;
              return (
                <div key={column.id} className="border border-slate-600/50 bg-[var(--bg-app)] px-2 py-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-[11px] font-semibold text-[var(--text-primary)]">{column.name}</span>
                    <span className="text-[10px] text-[var(--text-secondary)]">{assignedCount} atv.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={localFte[column.id] ?? String(Math.max(1, Number(fteByRole[column.id] ?? 1)))}
                      onChange={(e) => setLocalFte((prev) => ({ ...prev, [column.id]: e.target.value }))}
                      onBlur={() => {
                        const val = Math.max(1, Number(localFte[column.id]) || 1);
                        setLocalFte((prev) => ({ ...prev, [column.id]: String(val) }));
                        onFteChange(column.id, val);
                      }}
                      className="w-20 border border-slate-600/70 bg-[var(--bg-surface)] px-2 py-1.5 text-[11px] text-[var(--text-primary)] focus:border-blue-500 focus:outline-none"
                    />
                    <span className="text-[10px] text-[var(--text-secondary)]">FTE</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Demanda & Zoom ──────────────────────────────────────────── */}
        {(onDemandaPercentChange || onZoomChange) && (
          <section className="border border-slate-600/70 bg-[var(--bg-surface)] px-3 py-3">
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-[var(--text-primary)]">
              <SlidersHorizontal size={14} /> Demanda &amp; Zoom
            </div>
            {onDemandaPercentChange && (
              <div className="mb-3">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Demanda</span>
                  <div className="flex items-center gap-1">
                    <span className={`font-mono text-[11px] font-bold tabular-nums ${demandaPercent > 0 ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                      +{demandaPercent}%
                    </span>
                    {demandaPercent > 0 && (
                      <button
                        type="button"
                        onClick={() => onDemandaPercentChange(0)}
                        className="flex h-4 w-4 items-center justify-center rounded text-amber-400 hover:bg-amber-500/20 transition-colors"
                        title="Resetar simulação"
                      >
                        <RotateCcw size={9} />
                      </button>
                    )}
                  </div>
                </div>
                <input
                  type="range"
                  min={0}
                  max={200}
                  step={5}
                  value={demandaPercent}
                  onChange={(e) => onDemandaPercentChange(Number(e.target.value))}
                  className="w-full accent-amber-500"
                  title={`Aumento de demanda: +${demandaPercent}%`}
                />
              </div>
            )}
            {onZoomChange && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Zoom</span>
                  <span className="font-mono text-[11px] text-[var(--text-secondary)]">{Math.round(zoom * 100)}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onZoomChange(Math.max(0.25, zoom - 0.25))}
                    className="flex h-7 w-7 items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-app)] text-[var(--text-secondary)] hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)] transition-colors"
                    title="Reduzir escala"
                  >
                    <ZoomOut size={12} />
                  </button>
                  <input
                    type="range"
                    min={25}
                    max={300}
                    step={25}
                    value={Math.round(zoom * 100)}
                    onChange={(e) => onZoomChange(Number(e.target.value) / 100)}
                    className="flex-1 accent-[var(--nav-active-bar)]"
                  />
                  <button
                    type="button"
                    onClick={() => onZoomChange(Math.min(3.0, zoom + 0.25))}
                    className="flex h-7 w-7 items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-app)] text-[var(--text-secondary)] hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)] transition-colors"
                    title="Aumentar escala"
                  >
                    <ZoomIn size={12} />
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

      </div>

      {/* ── Ações ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-t border-[var(--border-default)] px-3 pt-3 pb-4 flex flex-col gap-3">
        {pendingChanges > 0 && (
          <p className="text-[11px] leading-none text-[var(--text-secondary)] opacity-70">
            {pendingChanges} alteração(es) pronta(s) para sincronizar
          </p>
        )}
        {(onAutoBalance || onSaveBaseline) && (
          <div className="flex items-center gap-2">
            {onAutoBalance && (
              <button
                type="button"
                onClick={onAutoBalance}
                className="inline-flex flex-1 h-[30px] items-center justify-center gap-1.5 rounded border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[11px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]"
              >
                <Shuffle size={11} /> Equilibrar
              </button>
            )}
            {onSaveBaseline && (
              <button
                type="button"
                onClick={onSaveBaseline}
                title="Salvar distribuição atual como baseline de comparação"
                className={`inline-flex flex-1 h-[30px] items-center justify-center gap-1.5 rounded border text-[11px] font-semibold transition-colors ${
                  savedBaseline
                    ? 'border-[var(--nav-active-bar)]/40 bg-[var(--nav-active-bar)]/10 text-[var(--nav-active-bar)] hover:bg-[var(--nav-active-bar)]/20'
                    : 'border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-primary)] hover:bg-[var(--button-hover)]'
                }`}
              >
                <Bookmark size={11} /> {savedBaseline ? 'Atualizar Baseline' : 'Salvar Baseline'}
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onSyncToMap}
          disabled={pendingChanges === 0}
          className="inline-flex h-[36px] w-full items-center justify-center gap-2 rounded-[6px] px-4 text-[12px] font-semibold text-white transition-all hover:brightness-110 disabled:opacity-40 disabled:hover:brightness-100"
          style={{ background: 'linear-gradient(135deg, #2563EB, #1D4ED8)' }}
        >
          <Map size={13} /> Sincronizar Mapa
        </button>
        {onSyncToExecution && (
          <button
            type="button"
            onClick={onSyncToExecution}
            disabled={syncingToExecution}
            className="inline-flex h-[30px] w-full items-center justify-center gap-1.5 rounded border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[11px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)] disabled:opacity-40"
          >
            {syncingToExecution ? <RefreshCw size={11} className="animate-spin" /> : <GitMerge size={11} />}
            Sincronizar Workflow
          </button>
        )}
      </div>
    </div>
  );
};

export default YamazumiPanel;
